import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';

export default function ExceptionTrackingFeaturePage() {
  const faqs = [
    {
      question: 'What is exception tracking?',
      answer: 'Exception tracking documents changes that bypass normal controls, including missing approvals, failed checks, or direct pushes to protected branches.'
    },
    {
      question: 'How are exceptions detected?',
      answer: 'EvidentTrail evaluates pull request events and protection settings to flag changes that violate configured control criteria.'
    },
    {
      question: 'Can exceptions be acknowledged with a reason?',
      answer: 'Yes. Exceptions can be acknowledged with context such as emergency fixes or pre-approved changes.'
    },
    {
      question: 'What do auditors want to see?',
      answer: 'Auditors expect exceptions to be documented with timestamps, identities, and a clear rationale so they can verify control intent.'
    }
  ];

  return (
    <MarketingLayout
      title="Exception Tracking - Document Control Bypasses for Audits"
      description="EvidentTrail exception tracking documents when changes bypass your normal controls. Provide auditors with explanations for exceptions, not surprises."
      h1="Exception Tracking"
      heroText="Not every change follows the happy path. Exception tracking documents when controls are bypassed, so you can explain them to auditors with context instead of surprises."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Exception Tracking', url: 'https://evidenttrail.com/features/exception-tracking' }
        ]}
      />
      <FaqJsonLd items={faqs} />
      {/* What It Tracks */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>What Exceptions Are Tracked</h2>
            <div className={tokens.card}>
              <p className={`${tokens.text.base} mb-6`}>
                EvidentTrail identifies changes that don't follow your expected controls:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Merged without approval</strong> — PR merged before required reviews were completed
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">CI checks bypassed</strong> — Merge occurred with failing or pending checks
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Direct push to protected branch</strong> — Commit pushed without going through PR workflow
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Self-approved</strong> — PR author approved their own changes
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Branch protection changes</strong> — Protection rules were modified or relaxed (tracked as drift)
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">AI-assisted context</strong> — Includes context for AI-assisted changes when present
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Exceptions Matter */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Why Exceptions Matter for Audits</h2>
            <div className="space-y-6">
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Auditors Expect Exceptions</h3>
                <p className={tokens.text.base}>
                  No process is perfect. Auditors know that emergency hotfixes and urgent changes happen.
                  What they want to see is that you <strong className="text-white">documented</strong> these
                  exceptions and have a reasonable explanation.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Unexplained Exceptions Are Findings</h3>
                <p className={tokens.text.base}>
                  When an auditor discovers a change that bypassed controls and you have no explanation,
                  it becomes an audit finding. Documented exceptions with context are generally accepted.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Review Patterns Over Time</h3>
                <p className={tokens.text.base}>
                  With exceptions tracked in one place, you can review trends during retrospectives.
                  If the same repository or scenario appears frequently, you can investigate root causes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>How Exception Tracking Works</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className={tokens.card}>
                <div className="w-12 h-12 bg-primary-600/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h3 className={`${tokens.heading.h3} mb-2`}>Automatic Detection</h3>
                <p className={tokens.text.base}>
                  EvidentTrail automatically flags changes that don't meet your configured control criteria.
                </p>
              </div>
              <div className={tokens.card}>
                <div className="w-12 h-12 bg-primary-600/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <h3 className={`${tokens.heading.h3} mb-2`}>Notifications</h3>
                <p className={tokens.text.base}>
                  Get alerted when exceptions occur so you can investigate promptly.
                </p>
              </div>
              <div className={tokens.card}>
                <div className="w-12 h-12 bg-primary-600/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h3 className={`${tokens.heading.h3} mb-2`}>Add Context</h3>
                <p className={tokens.text.base}>
                  Acknowledge exceptions with a reason: "Emergency security patch" or "Pre-approved by CTO".
                </p>
              </div>
              <div className={tokens.card}>
                <div className="w-12 h-12 bg-primary-600/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className={`${tokens.heading.h3} mb-2`}>Audit Report</h3>
                <p className={tokens.text.base}>
                  Generate a summary of all exceptions with their explanations for auditor review.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Exception */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Sample Exception Record</h2>
            <div className={`${tokens.card} font-mono text-sm`}>
              <pre className="text-slate-300 overflow-x-auto">
                {`{
  "exceptionType": "merged_without_approval",
  "timestamp": "2026-01-02T02:15:44Z",
  "repository": "acme-corp/api-service",
  "pullRequest": {
    "number": 842,
    "title": "URGENT: Fix authentication bypass",
    "author": "security.engineer",
    "mergedBy": "security.engineer"
  },
  "controlExpected": "minimum 1 approval required",
  "acknowledged": true,
  "acknowledgedBy": "cto@acme.com",
  "reason": "Emergency security patch - verbal approval from CTO at 2:10 AM"
}`}
              </pre>
            </div>
            <p className={`${tokens.text.small} mt-4 text-slate-400`}>
              <Link to="/sample-exception" className="text-primary-400 hover:underline">
                View the full sample exception record →
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs.map((item) => (
                <div key={item.question} className={tokens.card}>
                  <h3 className={`${tokens.heading.h3} mb-2`}>{item.question}</h3>
                  <p className={tokens.text.base}>{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className={`${tokens.heading.h2} mb-4`}>Document Exceptions Before Your Audit</h2>
            <p className={`${tokens.text.base} mb-8`}>
              Start tracking and documenting control exceptions automatically.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={appLinks.register} className="btn btn-primary text-lg px-8 py-3">
                Get Started
              </a>
              <Link to="/features/audit-timeline" className="text-slate-300 hover:text-white transition-colors">
                Learn about Audit Timeline →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
